import { NavigableModal, Text } from '$components'
import { useLocation, useNavigate } from 'react-router'
import dayjs from 'dayjs'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { appointmentCreateMutation, appointmentIndexQueryKey } from '$api-client/@tanstack/react-query.gen'
import type { CreateRequest, DogBreed } from '$api-client/types.gen'
import { CreateAppointmentForm, CreateAppointmentSchema, Input } from '$forms/create-appointment'
import { ToastHelper } from '$components'
import { ToastType } from '$components'

export function CreatePetAppointmentModal() {
  const today = dayjs.utc()
  const { state } = useLocation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const pet = state?.pet
  const client = state?.client

  const onClose = () => navigate(-1)

  const scheduleMutation = useMutation({
    ...appointmentCreateMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: appointmentIndexQueryKey(),
      })
      ToastHelper.createSuccess(ToastType.Appointment)
      onClose()
    },
    onError: () => ToastHelper.createError(ToastType.Appointment),
  })

  const handleScheduleAppointment = (form: CreateAppointmentSchema) => {
    const body: CreateRequest = {
      services: form.services.map(s => Number(s.id)),
      finish_at: form.slot.finish_at,
      start_at: form.slot.start_at,
      groomer_id: Number(form.slot.groomer.id),
      price_in_cents: form.price,
      notes: form.notes,
    }

    // Handle client data
    if (form[Input._NewClient]) {
      body.new_client = {
        first_name: form.new_client_first_name,
        last_name: form.new_client_last_name,
        email: form.new_client_email,
        phone: form.new_client_phone,
      }
    } else {
      body.client_id = form.client?.id
    }

    // Handle pet data
    if (form[Input._NewPet]) {
      body.new_pet = {
        name: form.new_pet_name!,
        breed: form.new_pet_breed!.value as DogBreed,
        size_category_id: form.new_pet_size_category?.id ? Number(form.new_pet_size_category.id) : undefined,
        coat_type_id: form.new_pet_coat_type?.id ? Number(form.new_pet_coat_type.id) : undefined,
      }
    } else {
      body.pet_id = Number(form.pet?.id)
    }

    scheduleMutation.mutate({ body })
  }

  const defaultValues = {
    date: today.toISOString(),
    ...(pet && { pet: { id: pet.id.toString(), value: pet.name, image: pet.image } }),
    ...(client && { client: { id: client.id, value: `${client.first_name} ${client.last_name}`, image: client.image } }),
  }

  return (
    <CreateAppointmentForm
      onSubmit={handleScheduleAppointment}
      onCancel={onClose}
      defaultValues={defaultValues}
      isLoading={scheduleMutation.isPending}
    >
      <NavigableModal onClose={onClose}>
        <NavigableModal.Header>
          <Text as="h2">
            Schedule Appointment
          </Text>
        </NavigableModal.Header>

        <NavigableModal.Content>
          <CreateAppointmentForm.Content />
        </NavigableModal.Content>

        <NavigableModal.Footer>
          <CreateAppointmentForm.Controls />
        </NavigableModal.Footer>
      </NavigableModal>
    </CreateAppointmentForm>
  )
}
